/**
 * MacIntyreConvectionPage — MacIntyre 1993 nocturnal convective mixing
 *
 * Surface buoyancy flux (W/kg) from net cooling:
 *   B_0 = (g · α · Q_n) / (ρ · c_p)
 *
 * Convective velocity scale (Deardorff 1970):
 *   w* = (B_0 · h)^(1/3)
 *
 * Compared to wind-shear velocity scale u* = √(τ/ρ).
 *
 * Reference: MacIntyre S. (1993) Limnol. Oceanogr. 38(4): 798-817, UC Santa Barbara.
 *            Deardorff (1970) J. Atmos. Sci. 27(8): 1211-1213.
 *            Imberger (1985) Limnol. Oceanogr. 30(4): 737-770.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Moon, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function MacIntyreConvectionPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('macintyre_convection');
  const [Q_n, setQn] = useState(120);          // W/m² net surface heat loss at night
  const [tempC, setTempC] = useState(18);
  const [h_mix, setH] = useState(2.5);         // m surface mixed layer depth
  const [windMs, setWind] = useState(2.0);     // m/s 10-m wind

  const R = useMemo(() => {
    const g = 9.81;
    const rho = 1000;
    const c_p = 4186;
    // Thermal expansion coefficient (per K) ≈ linear function of T
    const alpha = 1.5e-5 + 1.2e-5 * Math.max(0, tempC - 4);
    // Buoyancy flux (W/kg)
    const B_0 = (g * alpha * Q_n) / (rho * c_p);
    // Convective velocity (m/s)
    const w_star = Math.pow(B_0 * h_mix, 1 / 3);
    // Wind-shear u* (Wuest & Lorke 2003 simple drag): tau = rho_air · C_D · U²; u* = sqrt(tau / rho_water)
    const rho_air = 1.225;
    const C_D = 1.3e-3;
    const tau = rho_air * C_D * windMs * windMs;
    const u_star = Math.sqrt(tau / rho);
    // Ratio
    const ratio = w_star / Math.max(u_star, 1e-6);
    const dominant = ratio > 1 ? 'convection' : 'wind';
    return { B_0, w_star, u_star, ratio, dominant };
  }, [Q_n, tempC, h_mix, windMs]);

  const fmt = (n: number, d = 4) => isFinite(n) ? n.toExponential(d) : '—';
  const fmt2 = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Moon} title="Module — MacIntyre Nocturnal Convective Mixing"
        subtitle="Night cooling drives surface turbulence — important on calm summer nights"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('macintyre_convection')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="macintyre_convection" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Buoyancy flux B₀</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.B_0)}</div>
          <div className="text-xs text-slate-500 mt-1">W/kg</div>
        </div>
        <div className="rounded-xl border-2 border-sky-700 bg-sky-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-sky-300">Convective w*</div>
          <div className="text-2xl font-bold text-sky-200 mt-1">{(R.w_star * 1000).toFixed(2)} mm/s</div>
          <div className="text-xs text-slate-500 mt-1">night-time turbulence</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Wind u*</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{(R.u_star * 1000).toFixed(2)} mm/s</div>
          <div className="text-xs text-slate-500 mt-1">U₁₀ = {windMs.toFixed(1)} m/s</div>
        </div>
        <div className={`rounded-xl border-2 p-4 ${R.dominant === 'convection' ? 'border-amber-700 bg-amber-900/15' : 'border-emerald-700 bg-emerald-900/15'}`}>
          <div className="text-xs uppercase tracking-wider text-slate-300">Dominant mixing</div>
          <div className={`text-lg font-bold mt-1 uppercase ${R.dominant === 'convection' ? 'text-amber-200' : 'text-emerald-200'}`}>{R.dominant}</div>
          <div className="text-xs text-slate-500 mt-1">w*/u* = {fmt2(R.ratio, 2)}</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">Net heat loss Qₙ: <strong className="text-slate-100">{Q_n} W/m²</strong></label>
          <input type="range" min={20} max={300} step={5} value={Q_n} onChange={e => setQn(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Surface T: <strong className="text-slate-100">{tempC} °C</strong></label>
          <input type="range" min={5} max={28} step={0.5} value={tempC} onChange={e => setTempC(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Mixed-layer h: <strong className="text-slate-100">{h_mix.toFixed(1)} m</strong></label>
          <input type="range" min={0.5} max={15} step={0.1} value={h_mix} onChange={e => setH(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Wind U₁₀: <strong className="text-slate-100">{windMs.toFixed(1)} m/s</strong></label>
          <input type="range" min={0} max={12} step={0.2} value={windMs} onChange={e => setWind(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Why aeration design needs this</div>
        <ul className="list-disc ml-5 space-y-1">
          <li>Wind-only mixing models predict near-zero turbulence on calm summer nights → over-estimate stratification stability.</li>
          <li>MacIntyre 1993 showed nocturnal cooling on calm clear nights drives w* &gt; u*, with TKE dissipation ε ≈ 10⁻⁶ W/kg.</li>
          <li>This nightly mixing entrains a sub-surface layer of warmer water back into the SML, deepening h_mix by 0.1-0.5 m per night.</li>
          <li>For an aeration scheme designed against worst-case (calm + high Q_n + stratified), convection alone may give 30-50% of the required mixing.</li>
          <li>The Wind-tilt-correction module models daytime tilt; this module fills the night-time gap.</li>
        </ul>
        <p className="mt-2">When enabled the Thermal Model adds w* in quadrature to u* when computing the effective surface stress used for
        Spigel-Imberger SML deepening. No conflicts with other modules — orthogonal physics.</p>
        <p className="italic text-slate-500 mt-2">Reference: MacIntyre S. (1993) Limnol. Oceanogr. 38(4): 798-817; Imberger (1985) Limnol. Oceanogr. 30(4): 737-770.</p>
      </div>
    </div>
  );
}
